import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AcademicCapIcon,
  ArrowUpTrayIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { apiDownload, apiGet, apiPost, apiUpload } from '../utils/api';

type GuidanceStatus = {
  ai_enabled: boolean;
  ai_provider: string;
  external_provider: boolean;
  external_approved: boolean;
  scanning_available: boolean;
  private_storage: string;
  supported_types: string[];
  roles: { read: string[]; write: string[]; approve: string[] };
};

type DocumentVersion = {
  id: number;
  version_no: number;
  file_name: string;
  mime_type: string;
  page_count: number | null;
  status: string;
  scan_status: string;
  extraction_status: string;
  effective_date: string | null;
  publication_date: string | null;
  approved_at: string | null;
  retired_at: string | null;
  created_at: string;
  chunk_count?: number;
};

type GuidanceDocument = {
  id: number;
  title: string;
  document_type: string;
  crop: string | null;
  topic: string | null;
  language: string;
  issuer: string | null;
  provenance: string | null;
  provenance_label: string | null;
  provenance_verified: boolean;
  status: string;
  current_version_id: number | null;
  approved_at: string | null;
  updated_at: string;
  version: DocumentVersion | null;
  versions?: DocumentVersion[];
  lessons?: Lesson[];
};

type Citation = {
  chunk_id: number;
  document_id: number;
  version_id: number;
  title: string;
  version: number;
  page_from: number;
  page_to: number;
  section: string | null;
  issuer: string | null;
  provenance_label: string | null;
  excerpt: string;
};

type AskAnswer = {
  answer: string;
  grounded: boolean;
  cannot_verify: boolean;
  citations: Citation[];
  uncertainty: string;
  requires_human_review: boolean;
  review_reason: string | null;
  provider: string;
  model_version: string | null;
};

type Lesson = {
  id: number;
  document_id: number | null;
  title: string;
  body: string;
  media_url: string | null;
  language: string;
  status: string;
  approved_at: string | null;
  updated_at: string;
};

type AuditEntry = {
  id: number;
  action: string;
  document_id: number | null;
  version_id: number | null;
  chunk_ids: number[];
  metadata: Record<string, any>;
  created_at: string;
  actor: string | null;
  actor_role: string | null;
};

const TABS = [
  { id: 'library', label: 'Documents', icon: BookOpenIcon },
  { id: 'review', label: 'Review queue', icon: ShieldCheckIcon },
  { id: 'ask', label: 'Ask', icon: QuestionMarkCircleIcon },
  { id: 'lessons', label: 'Lessons', icon: AcademicCapIcon },
  { id: 'audit', label: 'Access audit', icon: ClipboardDocumentCheckIcon },
] as const;

type TabId = (typeof TABS)[number]['id'];

const statusTone = (status: string) => {
  switch (status) {
    case 'approved':
    case 'passed':
    case 'extracted':
      return 'bg-green-100 text-green-800';
    case 'pending_review':
    case 'pending':
      return 'bg-amber-100 text-amber-800';
    case 'rejected':
    case 'failed':
    case 'quarantined':
      return 'bg-red-100 text-red-800';
    case 'retired':
      return 'bg-slate-200 text-slate-700';
    default:
      return 'bg-blue-100 text-blue-800';
  }
};

const Badge: React.FC<{ value: string; label?: string }> = ({ value, label }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusTone(value)}`}>
    {label || value.replace(/_/g, ' ')}
  </span>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; actions?: React.ReactNode }> = ({
  title,
  subtitle,
  children,
  actions,
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions}
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
    {children}
  </div>
);

const GuidancePage: React.FC = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'project_manager' || user?.role === 'super_admin';

  const [tab, setTab] = useState<TabId>('library');
  const [status, setStatus] = useState<GuidanceStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [documents, setDocuments] = useState<GuidanceDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GuidanceDocument | null>(null);
  const [filters, setFilters] = useState({ q: '', crop: '', topic: '', language: '', issuer: '', status: '' });

  const [showUpload, setShowUpload] = useState(false);
  const [upload, setUpload] = useState({
    title: '',
    issuer: '',
    crop: '',
    topic: '',
    language: 'en',
    document_type: 'extension_guidance',
    effective_date: '',
    publication_date: '',
    provenance: '',
    provenance_label: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [askQuestion, setAskQuestion] = useState('');
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<AskAnswer | null>(null);

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [lessonDraft, setLessonDraft] = useState({ document_id: '', title: '', body: '', language: 'en', media_url: '', assignment_id: '', farmer_id: '' });
  const [lessonMessage, setLessonMessage] = useState<string | null>(null);

  const [audit, setAudit] = useState<AuditEntry[]>([]);

  const loadStatus = useCallback(async () => {
    try {
      const data = await apiGet<GuidanceStatus>('/api/guidance/status');
      setStatus(data);
      setStatusError(null);
    } catch (error: any) {
      setStatusError(error?.message || 'Guidance is unavailable');
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const params: Record<string, string> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const data = await apiGet<{ documents: GuidanceDocument[] }>('/api/guidance/documents', params);
      setDocuments(data.documents || []);
      setDocumentsError(null);
    } catch (error: any) {
      setDocumentsError(error?.message || 'Documents could not be loaded');
    } finally {
      setDocumentsLoading(false);
    }
  }, [filters]);

  const loadLessons = useCallback(async () => {
    try {
      const data = await apiGet<{ lessons: Lesson[] }>('/api/guidance/lessons');
      setLessons(data.lessons || []);
    } catch {
      setLessons([]);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    if (!isManager) return;
    try {
      const data = await apiGet<{ entries: AuditEntry[] }>('/api/guidance/audit');
      setAudit(data.entries || []);
    } catch {
      setAudit([]);
    }
  }, [isManager]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    apiGet<{ document: GuidanceDocument }>(`/api/guidance/documents/${selected.id}`)
      .then((data) => {
        if (!cancelled) setSelected(data.document);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  useEffect(() => {
    if (tab === 'lessons') loadLessons();
    if (tab === 'audit') loadAudit();
  }, [tab, loadLessons, loadAudit]);

  const pending = useMemo(
    () => documents.filter((doc) => doc.status === 'pending_review'),
    [documents]
  );
  const approved = useMemo(
    () => documents.filter((doc) => doc.status === 'approved'),
    [documents]
  );

  const submitUpload = async () => {
    if (!uploadFile || !upload.title.trim()) {
      setUploadError('A title and a PDF or text file are required.');
      return;
    }
    setUploadBusy(true);
    setUploadError(null);
    try {
      await apiUpload('/api/guidance/documents', uploadFile, upload);
      setShowUpload(false);
      setUploadFile(null);
      setUpload({ ...upload, title: '', issuer: '', crop: '', topic: '', provenance: '', provenance_label: '' });
      await loadDocuments();
      setTab('review');
    } catch (error: any) {
      // The server fails closed on scan/extraction problems; show its reason.
      setUploadError(error?.message || 'The document could not be uploaded.');
    } finally {
      setUploadBusy(false);
    }
  };

  const approve = async (doc: GuidanceDocument) => {
    const confirmed = window.confirm(
      `Confirm that ${doc.title} may be stored and used, and that its provenance (${doc.provenance_label || 'not stated'}) is accurate.`
    );
    if (!confirmed) return;
    try {
      await apiPost(`/api/guidance/documents/${doc.id}/approve`, { provenance_confirmed: true });
      await loadDocuments();
      if (selected?.id === doc.id) setSelected(null);
    } catch (error: any) {
      alert(error?.message || 'Approval failed');
    }
  };

  const reject = async (doc: GuidanceDocument) => {
    const reason = window.prompt('Why is this document being rejected?');
    if (!reason) return;
    try {
      await apiPost(`/api/guidance/documents/${doc.id}/reject`, { reason });
      await loadDocuments();
      setSelected(null);
    } catch (error: any) {
      alert(error?.message || 'Rejection failed');
    }
  };

  const retire = async (doc: GuidanceDocument) => {
    const reason = window.prompt('Why is this document being retired? Its chunks will leave active retrieval.');
    if (!reason) return;
    try {
      await apiPost(`/api/guidance/documents/${doc.id}/retire`, { reason });
      await loadDocuments();
      setSelected(null);
    } catch (error: any) {
      alert(error?.message || 'Retirement failed');
    }
  };

  const download = async (doc: GuidanceDocument, version?: DocumentVersion) => {
    try {
      const suffix = version ? `?version_id=${version.id}` : '';
      await apiDownload(`/api/guidance/documents/${doc.id}/download${suffix}`, version?.file_name || `${doc.title}.pdf`);
      await loadAudit();
    } catch (error: any) {
      alert(error?.message || 'The document could not be downloaded');
    }
  };

  const ask = async () => {
    if (!askQuestion.trim()) return;
    setAskBusy(true);
    setAskError(null);
    setAnswer(null);
    try {
      const data = await apiPost<AskAnswer>('/api/guidance/ask', {
        question: askQuestion.trim(),
        language: user?.language || 'en',
      });
      setAnswer(data);
      await loadAudit();
    } catch (error: any) {
      setAskError(error?.message || 'The question could not be answered');
    } finally {
      setAskBusy(false);
    }
  };

  const createLesson = async () => {
    setLessonMessage(null);
    try {
      await apiPost('/api/guidance/lessons', {
        document_id: Number(lessonDraft.document_id),
        title: lessonDraft.title,
        body: lessonDraft.body,
        language: lessonDraft.language,
        media_url: lessonDraft.media_url || undefined,
      });
      setLessonDraft({ ...lessonDraft, title: '', body: '', media_url: '' });
      setLessonMessage('Lesson submitted for manager review.');
      await loadLessons();
    } catch (error: any) {
      setLessonMessage(error?.message || 'The lesson could not be created');
    }
  };

  const approveLesson = async (lesson: Lesson) => {
    try {
      await apiPost(`/api/guidance/lessons/${lesson.id}/approve`, {});
      await loadLessons();
    } catch (error: any) {
      alert(error?.message || 'Approval failed');
    }
  };

  const assignLesson = async (lesson: Lesson) => {
    if (!lessonDraft.assignment_id) {
      setLessonMessage('Enter the job/assignment id to attach this lesson to.');
      return;
    }
    try {
      await apiPost('/api/guidance/lessons/assign', {
        lesson_id: lesson.id,
        assignment_id: Number(lessonDraft.assignment_id),
        farmer_id: lessonDraft.farmer_id ? Number(lessonDraft.farmer_id) : undefined,
      });
      setLessonMessage('Lesson attached to the job. The farmer can now acknowledge it.');
    } catch (error: any) {
      setLessonMessage(error?.message || 'The lesson could not be assigned');
    }
  };

  if (statusError) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-3 text-xl font-bold text-slate-900">Guidance is unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">{statusError}</p>
          <button onClick={loadStatus} className="mt-5 rounded-lg bg-[#00A1DE] px-5 py-2 font-semibold text-white">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 px-4 py-8 text-white sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-white/15 p-2">
                <BookOpenIcon className="h-7 w-7" />
              </span>
              <h1 className="text-2xl font-bold sm:text-3xl">Guidance & Training</h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-emerald-50">
              Private, versioned documents for this organisation. Answers cite an approved page; a question
              without relevant approved evidence is refused rather than answered from unrelated material.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/15 px-3 py-1 font-semibold">
              AI {status?.ai_enabled ? `on · ${status.ai_provider}` : 'off'}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 font-semibold">
              Scanner {status?.scanning_available ? 'configured' : 'not configured (uploads fail closed)'}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 font-semibold">
              Storage {status?.private_storage?.replace(/_/g, ' ') || 'private'}
            </span>
          </div>
        </div>
      </header>

      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-6 lg:px-8">
          {TABS.map((item) => {
            const Icon = item.icon;
            if (item.id === 'audit' && !isManager) return null;
            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold ${
                  tab === item.id
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
                {item.id === 'review' && pending.length > 0 && (
                  <span className="rounded-full bg-amber-100 px-1.5 text-xs text-amber-800">{pending.length}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {tab === 'library' && (
          <>
            <Section
              title="Approved documents"
              subtitle={`${approved.length} approved · ${pending.length} waiting for review`}
              actions={
                <button
                  onClick={() => setShowUpload((value) => !value)}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  <ArrowUpTrayIcon className="h-4 w-4" />
                  Upload document
                </button>
              }
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <label className="relative sm:col-span-2">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
                    placeholder="Search title, issuer, topic"
                    value={filters.q}
                    onChange={(event) => setFilters({ ...filters, q: event.target.value })}
                  />
                </label>
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Crop"
                  value={filters.crop}
                  onChange={(event) => setFilters({ ...filters, crop: event.target.value })}
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Topic / task"
                  value={filters.topic}
                  onChange={(event) => setFilters({ ...filters, topic: event.target.value })}
                />
                <input
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Issuer"
                  value={filters.issuer}
                  onChange={(event) => setFilters({ ...filters, issuer: event.target.value })}
                />
                <select
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={filters.status}
                  onChange={(event) => setFilters({ ...filters, status: event.target.value })}
                >
                  <option value="">All statuses</option>
                  {['approved', 'pending_review', 'rejected', 'retired'].map((value) => (
                    <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {showUpload && (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <h3 className="font-bold text-slate-900">Upload a private document</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    The server scans, stores privately, extracts page text and indexes it. It is not searchable
                    until a manager approves it. A document from NAEB is labelled “Received from NAEB” until
                    provenance is confirmed.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm sm:col-span-2" placeholder="Title *" value={upload.title} onChange={(e) => setUpload({ ...upload, title: e.target.value })} />
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Issuer (e.g. NAEB)" value={upload.issuer} onChange={(e) => setUpload({ ...upload, issuer: e.target.value })} />
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Crop" value={upload.crop} onChange={(e) => setUpload({ ...upload, crop: e.target.value })} />
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Topic / task" value={upload.topic} onChange={(e) => setUpload({ ...upload, topic: e.target.value })} />
                    <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={upload.language} onChange={(e) => setUpload({ ...upload, language: e.target.value })}>
                      <option value="en">English</option>
                      <option value="rw">Kinyarwanda</option>
                      <option value="fr">Français</option>
                    </select>
                    <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={upload.document_type} onChange={(e) => setUpload({ ...upload, document_type: e.target.value })}>
                      <option value="extension_guidance">Extension guidance</option>
                      <option value="crop_protocol">Crop protocol</option>
                      <option value="sop">Organisation SOP</option>
                      <option value="research">Research</option>
                      <option value="other">Other</option>
                    </select>
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="date" value={upload.effective_date} onChange={(e) => setUpload({ ...upload, effective_date: e.target.value })} />
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Provenance note" value={upload.provenance} onChange={(e) => setUpload({ ...upload, provenance: e.target.value })} />
                    <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Provenance label (optional)" value={upload.provenance_label} onChange={(e) => setUpload({ ...upload, provenance_label: e.target.value })} />
                    <input
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      type="file"
                      accept=".pdf,.txt,.md,.csv,application/pdf,text/plain,text/markdown,text/csv"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    />
                  </div>
                  {uploadError && <p className="mt-3 text-sm font-medium text-red-700">{uploadError}</p>}
                  <div className="mt-4 flex gap-2">
                    <button onClick={submitUpload} disabled={uploadBusy} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                      {uploadBusy ? 'Uploading and indexing…' : 'Upload'}
                    </button>
                    <button onClick={() => setShowUpload(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-5">
                {documentsLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading documents…</div>
                ) : documentsError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {documentsError}
                    <button onClick={loadDocuments} className="ml-3 font-semibold underline">Retry</button>
                  </div>
                ) : documents.length === 0 ? (
                  <Empty>No documents yet. Upload approved guidance to make it searchable.</Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="px-2 py-2">Title</th>
                          <th className="px-2 py-2">Crop / topic</th>
                          <th className="px-2 py-2">Issuer</th>
                          <th className="px-2 py-2">Version</th>
                          <th className="px-2 py-2">Status</th>
                          <th className="px-2 py-2">Provenance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr
                            key={doc.id}
                            className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                            onClick={() => setSelected(doc)}
                          >
                            <td className="px-2 py-3 font-semibold text-slate-900">{doc.title}</td>
                            <td className="px-2 py-3 text-slate-600">{[doc.crop, doc.topic].filter(Boolean).join(' · ') || '—'}</td>
                            <td className="px-2 py-3 text-slate-600">{doc.issuer || '—'}</td>
                            <td className="px-2 py-3 text-slate-600">{doc.version ? `v${doc.version.version_no} · ${doc.version.page_count || '?'} pp` : '—'}</td>
                            <td className="px-2 py-3"><Badge value={doc.status} /></td>
                            <td className="px-2 py-3">
                              <span className="text-xs text-slate-600">{doc.provenance_label || 'Not stated'}</span>
                              {!doc.provenance_verified && <span className="ml-1 text-xs font-semibold text-amber-700">unverified</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Section>

            {selected && (
              <Section
                title={selected.title}
                subtitle={`${selected.document_type.replace(/_/g, ' ')} · ${selected.language.toUpperCase()}`}
                actions={
                  <div className="flex flex-wrap gap-2">
                    {selected.current_version_id && (
                      <button onClick={() => download(selected)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700">
                        Download
                      </button>
                    )}
                    {isManager && selected.status === 'pending_review' && (
                      <>
                        <button onClick={() => approve(selected)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white">Approve</button>
                        <button onClick={() => reject(selected)} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white">Reject</button>
                      </>
                    )}
                    {isManager && selected.status === 'approved' && (
                      <button onClick={() => retire(selected)} className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700">Retire</button>
                    )}
                    <button onClick={() => setSelected(null)} className="rounded-lg px-3 py-1.5 text-sm text-slate-500">Close</button>
                  </div>
                }
              >
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div><p className="text-xs uppercase text-slate-500">Crop</p><p className="font-semibold">{selected.crop || '—'}</p></div>
                  <div><p className="text-xs uppercase text-slate-500">Topic</p><p className="font-semibold">{selected.topic || '—'}</p></div>
                  <div><p className="text-xs uppercase text-slate-500">Issuer</p><p className="font-semibold">{selected.issuer || '—'}</p></div>
                  <div><p className="text-xs uppercase text-slate-500">Status</p><Badge value={selected.status} /></div>
                  <div className="sm:col-span-2">
                    <p className="text-xs uppercase text-slate-500">Provenance</p>
                    <p className="font-semibold">
                      {selected.provenance_label || 'Not stated'}{' '}
                      {selected.provenance_verified ? (
                        <span className="text-xs font-semibold text-emerald-700">confirmed by a manager</span>
                      ) : (
                        <span className="text-xs font-semibold text-amber-700">not verified</span>
                      )}
                    </p>
                    {selected.provenance && <p className="text-sm text-slate-600">{selected.provenance}</p>}
                  </div>
                  {selected.versions && (
                    <div className="sm:col-span-2">
                      <p className="text-xs uppercase text-slate-500">Version history</p>
                      <div className="mt-1 space-y-1">
                        {selected.versions.map((version) => (
                          <div key={version.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-semibold">v{version.version_no}</span>
                            <Badge value={version.status} />
                            <span className="text-slate-500">{version.page_count || '?'} pages</span>
                            {version.effective_date && <span className="text-slate-500">effective {version.effective_date.slice(0, 10)}</span>}
                            <button onClick={() => download(selected, version)} className="text-emerald-700 underline">download</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {!!selected.lessons?.length && (
                  <div className="mt-4">
                    <p className="text-xs uppercase text-slate-500">Related lessons</p>
                    <ul className="mt-1 space-y-1 text-sm">
                      {selected.lessons.map((lesson) => (
                        <li key={lesson.id} className="flex items-center gap-2">
                          <DocumentTextIcon className="h-4 w-4 text-emerald-600" />
                          <span className="font-medium">{lesson.title}</span>
                          <Badge value={lesson.status} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-4 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                  Downloaded copies cannot be recalled. Retiring a version stops new retrieval and deactivates its
                  chunks, but a file already downloaded stays on the device that downloaded it.
                </p>
              </Section>
            )}
          </>
        )}

        {tab === 'review' && (
          <Section title="Review queue" subtitle="Pending uploads, version replacements and provenance confirmation">
            {pending.length === 0 ? (
              <Empty>No document is waiting for review.</Empty>
            ) : (
              <div className="space-y-3">
                {pending.map((doc) => (
                  <div key={doc.id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900">{doc.title}</h3>
                        <p className="text-sm text-slate-600">
                          {[doc.issuer, doc.crop, doc.topic, doc.language?.toUpperCase()].filter(Boolean).join(' · ')}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Provenance label: <strong>{doc.provenance_label || 'not stated'}</strong>
                          {doc.version ? ` · ${doc.version.page_count || '?'} pages · v${doc.version.version_no}` : ''}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setSelected(doc)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold">Preview</button>
                        {isManager && (
                          <>
                            <button onClick={() => approve(doc)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white">Approve</button>
                            <button onClick={() => reject(doc)} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white">Reject</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === 'ask' && (
          <Section
            title="Ask from approved documents only"
            subtitle={
              status?.ai_enabled
                ? `Provider: ${status.ai_provider}${status.external_provider ? (status.external_approved ? ' (external, approved)' : ' (external, NOT approved)') : ' (local extractive, no external call)'}`
                : 'Private guidance AI is disabled. A deployment administrator must enable it explicitly.'
            }
          >
            {!status?.ai_enabled ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-semibold">AI answers are off by default.</p>
                <p className="mt-1">
                  The library, uploads and review still work. Set <code>GUIDANCE_AI_ENABLED=true</code> only after the
                  organisation has approved how private documents may be processed.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm"
                    placeholder="e.g. How much manure should be applied to a maize plot before planting?"
                    value={askQuestion}
                    onChange={(event) => setAskQuestion(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') ask();
                    }}
                  />
                  <button onClick={ask} disabled={askBusy || !askQuestion.trim()} className="rounded-lg bg-emerald-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
                    {askBusy ? 'Searching approved documents…' : 'Ask'}
                  </button>
                </div>

                {askError && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{askError}</p>}

                {answer && (
                  <div className="mt-5 space-y-4">
                    <div className={`rounded-xl border p-4 ${answer.cannot_verify ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50/50'}`}>
                      <div className="flex items-center gap-2">
                        {answer.cannot_verify ? (
                          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                        )}
                        <p className="text-sm font-bold text-slate-900">
                          {answer.cannot_verify ? 'Not verified from your documents' : 'Grounded in approved documents'}
                        </p>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{answer.answer}</p>
                      <p className="mt-2 text-xs text-slate-500">{answer.uncertainty}</p>
                      {answer.requires_human_review && (
                        <p className="mt-3 rounded-lg bg-white/70 p-2 text-xs font-semibold text-amber-800">
                          Agronomist review required before acting. {answer.review_reason}
                        </p>
                      )}
                    </div>

                    {answer.citations.length > 0 && (
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">Sources</h3>
                        <div className="mt-2 space-y-2">
                          {answer.citations.map((citation) => (
                            <div key={citation.chunk_id} className="rounded-lg border border-slate-200 bg-white p-3">
                              <p className="text-sm font-semibold text-slate-900">
                                {citation.title} · v{citation.version} · page {citation.page_from}
                                {citation.page_to !== citation.page_from ? `–${citation.page_to}` : ''}
                                {citation.section ? ` · ${citation.section}` : ''}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {[citation.issuer, citation.provenance_label].filter(Boolean).join(' · ') || 'Provenance not stated'}
                              </p>
                              <blockquote className="mt-2 border-l-4 border-emerald-300 pl-3 text-sm text-slate-700">
                                {citation.excerpt}
                              </blockquote>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Section>
        )}

        {tab === 'lessons' && (
          <>
            <Section title="Reviewed short lessons" subtitle="Turn approved guidance into a simple instruction attached to a job">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <select
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={lessonDraft.document_id}
                  onChange={(e) => setLessonDraft({ ...lessonDraft, document_id: e.target.value })}
                >
                  <option value="">Approved document…</option>
                  {approved.map((doc) => (
                    <option key={doc.id} value={doc.id}>{doc.title}</option>
                  ))}
                </select>
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Lesson title" value={lessonDraft.title} onChange={(e) => setLessonDraft({ ...lessonDraft, title: e.target.value })} />
                <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={lessonDraft.language} onChange={(e) => setLessonDraft({ ...lessonDraft, language: e.target.value })}>
                  <option value="rw">Kinyarwanda</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
                <textarea className="rounded-lg border border-slate-300 px-3 py-2 text-sm sm:col-span-2 lg:col-span-3" rows={3} placeholder="Short instruction the farmer will see" value={lessonDraft.body} onChange={(e) => setLessonDraft({ ...lessonDraft, body: e.target.value })} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Voice/video URL (optional)" value={lessonDraft.media_url} onChange={(e) => setLessonDraft({ ...lessonDraft, media_url: e.target.value })} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Job/assignment id to attach later" value={lessonDraft.assignment_id} onChange={(e) => setLessonDraft({ ...lessonDraft, assignment_id: e.target.value })} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Farmer id (optional)" value={lessonDraft.farmer_id} onChange={(e) => setLessonDraft({ ...lessonDraft, farmer_id: e.target.value })} />
              </div>
              <button onClick={createLesson} className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">
                Submit lesson for review
              </button>
              {lessonMessage && <p className="mt-3 text-sm font-medium text-slate-700">{lessonMessage}</p>}
            </Section>

            <Section title="Lessons" subtitle="Farmers see only the simplified approved instruction">
              {lessons.length === 0 ? (
                <Empty>No lessons yet.</Empty>
              ) : (
                <div className="space-y-3">
                  {lessons.map((lesson) => (
                    <div key={lesson.id} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-slate-900">{lesson.title}</h3>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{lesson.body}</p>
                          {lesson.media_url && <a className="text-sm text-emerald-700 underline" href={lesson.media_url} target="_blank" rel="noreferrer">media</a>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge value={lesson.status} />
                          {isManager && ['draft', 'pending_review'].includes(lesson.status) && (
                            <button onClick={() => approveLesson(lesson)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
                          )}
                          {lesson.status === 'approved' && (
                            <button onClick={() => assignLesson(lesson)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold">Attach to job</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}

        {tab === 'audit' && isManager && (
          <Section title="Access audit" subtitle="Reads, downloads, approvals, retirements and AI source usage. Document text is never logged.">
            {audit.length === 0 ? (
              <Empty>No audit entries yet.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-slate-500">
                      <th className="px-2 py-2">When</th>
                      <th className="px-2 py-2">Action</th>
                      <th className="px-2 py-2">Actor</th>
                      <th className="px-2 py-2">Document</th>
                      <th className="px-2 py-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((entry) => (
                      <tr key={entry.id} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-slate-600">{new Date(entry.created_at).toLocaleString()}</td>
                        <td className="px-2 py-2 font-semibold">{entry.action}</td>
                        <td className="px-2 py-2 text-slate-600">{entry.actor || '—'} {entry.actor_role ? `(${entry.actor_role})` : ''}</td>
                        <td className="px-2 py-2 text-slate-600">{entry.document_id || '—'}</td>
                        <td className="px-2 py-2 text-xs text-slate-500">{JSON.stringify(entry.metadata)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        <footer className="rounded-xl bg-slate-100 p-4 text-xs text-slate-500">
          A private document stays private to this organisation. The AI gate is off by default; an external provider
          also requires an explicit business approval of document-sharing, retention and data-location terms. Code
          cannot create that approval.
        </footer>
      </main>
    </div>
  );
};

export default GuidancePage;
